import React, { useState, useEffect } from "react";

interface MedicalConditionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  condition: string;
  onSave: (condition: string, details: string) => void;
  initialDetails?: string;
}

const MedicalConditionPopup: React.FC<MedicalConditionPopupProps> = ({
  isOpen,
  onClose,
  condition,
  onSave,
  initialDetails = "",
}) => {
  const [details, setDetails] = useState(initialDetails);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimatingOut(false);
      // Start the animation after component mounts
      setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);
      // Reset details when opening
      setDetails(initialDetails);
    } else {
      setIsAnimatingIn(false);
    }
  }, [isOpen, initialDetails]);

  if (!isOpen && !isAnimatingOut) return null;

  // Check if details input has content (trim to ignore whitespace)
  const hasDetails = details.trim().length > 0;

  const handleSave = () => {
    // Only save if there are details
    if (hasDetails) {
      onSave(condition, details);
      handleClose();
    }
  };

  const handleClose = () => {
    setIsAnimatingIn(false);
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsAnimatingOut(false);
      onClose();
    }, 300);
  };

  const handleBackdropClick = () => {
    handleClose();
  };

  const handleDragHandleClick = () => {
    handleClose();
  };

  // Get placeholder text based on condition
  const getPlaceholderText = (condition: string) => {
    const placeholders: Record<string, string> = {
      PCOD: "Please describe your PCOD symptoms, current treatment, or any specific concerns...",
      PCOS: "Please share details about your PCOS symptoms, treatment history, or dietary restrictions...",
      Thyroid: "Please mention if it's hypo/hyperthyroid, current medication, or specific symptoms...",
      Diabetes: "Please specify Type 1/Type 2, current medication, blood sugar levels, or dietary needs...",
      Inflammation: "Please describe the type of inflammation, affected areas, or triggers you've noticed...",
      "Gut health": "Please share details about digestive issues, food sensitivities, or symptoms...",
      IBS: "Please describe your IBS symptoms, triggers, or foods that affect you...",
      "Fatty liver": "Please mention current treatment, dietary restrictions, or specific symptoms...",
      "Blood pressure": "Please specify if it's high/low blood pressure, current medication, or target levels...",
      "Heart health": "Please describe your heart condition, current treatment, or dietary restrictions...",
      Anemia: "Please mention the type of anemia, current treatment, or specific dietary needs...",
      Osteoporosis: "Please share details about bone density, current treatment, or calcium needs...",
      UTI: "Please describe frequency, symptoms, or any preventive measures you're taking...",
      "Kidney disease": "Please mention the stage, current treatment, or specific dietary restrictions...",
      "Auto immune": "Please specify the condition, current treatment, or foods to avoid...",
      "Food intolerance": "Please list specific foods or ingredients that cause reactions...",
      "Mental health": "Please share relevant information that might affect your nutrition plan...",
      Gout: "Please describe frequency of episodes, trigger foods, or current treatment...",
      Others: "Please describe your medical condition and any relevant details...",
    };
    
    return placeholders[condition] || "Please provide additional details about this condition...";
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimatingIn && !isAnimatingOut ? "bg-opacity-50" : "bg-opacity-0"
        }`}
        onClick={handleBackdropClick}
      />

      {/* Popup */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transform transition-transform duration-300 ease-out max-h-[80vh] ${
          isAnimatingIn && !isAnimatingOut ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="px-6 py-4 h-full flex flex-col">
          {/* Drag Handle */}
          <div className="flex justify-center mb-4">
            <div
              className="w-12 h-1 bg-gray-300 rounded-full cursor-pointer hover:bg-gray-400 transition-colors duration-200"
              onClick={handleDragHandleClick}
            ></div>
          </div>

          {/* Header */}
          {/* <div className="mb-6">
            <h3 className="text-[18px] font-semibold text-[#202020] mb-2">
              {condition} Details
            </h3>
            <p className="text-[12px] text-[#646464] font-normal">
              Please provide additional information about your {condition.toLowerCase()} condition to help us create a better plan for you.
            </p>
          </div> */}

          {/* Text Area */}
          <div className="flex-1 mb-6">
            {/* <label className="block text-[14px] font-medium text-[#828282] mb-3">
              Additional Information
            </label> */}
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={getPlaceholderText(condition)}
              rows={8}
              className="w-full px-4 py-3 text-[14px] text-[#202020] font-inter bg-[#F6F7F7] border border-[#E0E0E0] rounded-[12px] focus:outline-none focus:border-[#0DB5B5] resize-none placeholder:text-[#999999]"
            />
            <p className="text-[10px] text-[#999999] mt-2">
              This information will help our nutritionists provide personalized recommendations
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 py-3 px-4 border border-[#E0E0E0] text-[#646464] rounded-xl font-medium text-[14px] transition-colors duration-200 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasDetails}
              className={`flex-1 py-3 px-4 rounded-xl font-medium text-[14px] transition-colors duration-200 ${
                hasDetails
                  ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Save Details
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MedicalConditionPopup;